import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  TextField,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as ImportIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Close as FailIcon,
  Remove as PartialIcon,
} from '@mui/icons-material';
import PromptService from '../services/PromptService';

const colors = {
  surface: '#1e1e1e',
  surfaceElevated: '#2d2d2d',
  border: '#404040',
  textPrimary: '#e0e0e0',
  textSecondary: '#a0a0a0',
  textMuted: '#666',
  accent: '#8b5cf6',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
};

const PromptLibraryPanel = ({
  open,
  onClose,
  onSelectPrompt,
  onImportFromTool,
  tools = [],
}) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0: All, 1: Favorites
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);

  // Create dialog form
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    promptText: '',
    category: 'STANDALONE',
    domain: '',
  });

  useEffect(() => {
    if (open) {
      loadPrompts();
    }
  }, [open, activeTab, selectedCategory, searchQuery]);

  const loadPrompts = async () => {
    setLoading(true);
    setError(null);
    try {
      let result;
      if (activeTab === 1) {
        result = await PromptService.getFavoritePrompts();
      } else {
        result = await PromptService.listPrompts({
          category: selectedCategory || undefined,
          search: searchQuery || undefined,
        });
      }
      setPrompts(result.prompts || []);
    } catch (err) {
      console.error('Failed to load prompts:', err);
      setError(err.message || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = async () => {
    try {
      const prompt = await PromptService.createPrompt(createForm);
      setPrompts([prompt, ...prompts]);
      setCreateDialogOpen(false);
      setCreateForm({ name: '', description: '', promptText: '', category: 'STANDALONE', domain: '' });
    } catch (err) {
      console.error('Failed to create prompt:', err);
    }
  };

  const handleToggleFavorite = async (prompt, e) => {
    e.stopPropagation();
    try {
      const updated = await PromptService.togglePromptFavorite(prompt.id, !prompt.favorite);
      setPrompts(prompts.map(p => p.id === prompt.id ? updated : p));
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const handleDeletePrompt = async (prompt, e) => {
    e.stopPropagation();
    if (window.confirm(`Delete prompt "${prompt.name}"?`)) {
      try {
        await PromptService.deletePrompt(prompt.id);
        setPrompts(prompts.filter(p => p.id !== prompt.id));
      } catch (err) {
        console.error('Failed to delete prompt:', err);
      }
    }
  };

  const handleSelectPrompt = (prompt) => {
    onSelectPrompt(prompt);
    onClose();
  };

  const handleViewHistory = async (prompt, e) => {
    e.stopPropagation();
    setSelectedPrompt(prompt);
    try {
      const result = await PromptService.getPromptVersions(prompt.id);
      setVersions(result.versions || []);
      setVersionHistoryOpen(true);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const handleImportFromTool = async (toolId) => {
    try {
      const prompt = await PromptService.importPromptFromTool(toolId);
      setPrompts([prompt, ...prompts]);
      setImportDialogOpen(false);
      onSelectPrompt(prompt);
    } catch (err) {
      console.error('Failed to import from tool:', err);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'ORCHESTRATOR': return '#8b5cf6';
      case 'AGENT': return '#3b82f6';
      case 'WIZARD': return '#22c55e';
      case 'TEMPLATE': return '#f59e0b';
      default: return '#666';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return colors.success;
      case 'TESTING': return colors.warning;
      case 'ARCHIVED': return colors.textMuted;
      default: return colors.textSecondary;
    }
  };

  return (
    <>
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 400,
            bgcolor: colors.surface,
            borderRight: `1px solid ${colors.border}`,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: colors.textPrimary }}>Prompt Library</Typography>
            <IconButton onClick={onClose} sx={{ color: colors.textMuted }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              minHeight: 40,
              borderBottom: `1px solid ${colors.border}`,
              '& .MuiTab-root': { color: colors.textSecondary, minHeight: 40, py: 0 },
              '& .Mui-selected': { color: colors.accent },
              '& .MuiTabs-indicator': { bgcolor: colors.accent },
            }}
          >
            <Tab label="All Prompts" />
            <Tab label="Favorites" />
          </Tabs>

          {/* Search & Filter */}
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: colors.textMuted, mr: 1 }} />,
              }}
              sx={{
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.surfaceElevated,
                  '& fieldset': { borderColor: colors.border },
                },
                '& .MuiInputBase-input': { color: colors.textPrimary },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  displayEmpty
                  sx={{
                    bgcolor: colors.surfaceElevated,
                    color: colors.textPrimary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                  }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  <MenuItem value="STANDALONE">Standalone</MenuItem>
                  <MenuItem value="ORCHESTRATOR">Orchestrator</MenuItem>
                  <MenuItem value="AGENT">Agent</MenuItem>
                  <MenuItem value="WIZARD">Wizard</MenuItem>
                  <MenuItem value="TEMPLATE">Template</MenuItem>
                </Select>
              </FormControl>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
                sx={{ color: colors.accent }}
              >
                New
              </Button>
              <Button
                size="small"
                startIcon={<ImportIcon />}
                onClick={() => setImportDialogOpen(true)}
                sx={{ color: colors.textSecondary }}
              >
                Import
              </Button>
            </Box>
          </Box>

          {/* Prompt List */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: colors.error, mb: 1 }}>
                  Error loading prompts
                </Typography>
                <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                  {error}
                </Typography>
              </Box>
            ) : prompts.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: colors.textMuted }}>
                  {activeTab === 1 ? 'No favorite prompts' : 'No prompts found'}
                </Typography>
              </Box>
            ) : (
              <List dense>
                {prompts.map((prompt) => (
                  <ListItem
                    key={prompt.id}
                    button
                    onClick={() => handleSelectPrompt(prompt)}
                    sx={{
                      borderBottom: `1px solid ${colors.border}`,
                      '&:hover': { bgcolor: colors.surfaceElevated },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <DescriptionIcon sx={{ color: getCategoryColor(prompt.category), fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ color: colors.textPrimary, fontSize: '0.875rem' }}>
                            {prompt.name}
                          </Typography>
                          <Chip
                            label={prompt.status}
                            size="small"
                            sx={{
                              height: 16,
                              fontSize: '0.65rem',
                              bgcolor: 'transparent',
                              color: getStatusColor(prompt.status),
                              border: `1px solid ${getStatusColor(prompt.status)}`,
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }} noWrap>
                          {prompt.description || prompt.promptText?.substring(0, 60) + '...'}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Version History">
                        <IconButton size="small" onClick={(e) => handleViewHistory(prompt, e)}>
                          <HistoryIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={prompt.favorite ? 'Remove Favorite' : 'Add Favorite'}>
                        <IconButton size="small" onClick={(e) => handleToggleFavorite(prompt, e)}>
                          {prompt.favorite ? (
                            <StarIcon sx={{ fontSize: 18, color: colors.warning }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => handleDeletePrompt(prompt, e)}>
                          <DeleteIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Create Prompt Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.surface } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Create New Prompt</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={createForm.category}
              onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
              label="Category"
            >
              <MenuItem value="STANDALONE">Standalone</MenuItem>
              <MenuItem value="ORCHESTRATOR">Orchestrator</MenuItem>
              <MenuItem value="AGENT">Agent</MenuItem>
              <MenuItem value="WIZARD">Wizard</MenuItem>
              <MenuItem value="TEMPLATE">Template</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={6}
            label="Prompt Text"
            value={createForm.promptText}
            onChange={(e) => setCreateForm({ ...createForm, promptText: e.target.value })}
            placeholder="Enter your prompt template here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreatePrompt}
            disabled={!createForm.name || !createForm.promptText}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import from Tool Dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.surface } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>Import from Tool</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.textSecondary, mb: 2 }}>
            Select a tool to import its prompt into the library.
          </Typography>
          <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
            {tools.filter(t => t.prompt).map((tool) => (
              <ListItem
                key={tool.toolId}
                button
                onClick={() => handleImportFromTool(tool.toolId)}
                sx={{
                  borderBottom: `1px solid ${colors.border}`,
                  '&:hover': { bgcolor: colors.surfaceElevated },
                }}
              >
                <ListItemText
                  primary={<Typography sx={{ color: colors.textPrimary }}>{tool.toolName}</Typography>}
                  secondary={<Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>{tool.toolKey}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.surface } }}
      >
        <DialogTitle sx={{ color: colors.textPrimary }}>
          Version History: {selectedPrompt?.name}
        </DialogTitle>
        <DialogContent>
          <List dense>
            {versions.map((version) => (
              <ListItem
                key={version.id}
                sx={{
                  borderBottom: `1px solid ${colors.border}`,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, width: '100%' }}>
                  <Chip
                    label={`v${version.versionNumber}`}
                    size="small"
                    sx={{ bgcolor: colors.accent, color: '#fff' }}
                  />
                  <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                    {new Date(version.createdAt).toLocaleString()}
                  </Typography>
                  {version.changeNotes && (
                    <Typography sx={{ color: colors.textSecondary, fontSize: '0.75rem', ml: 1 }}>
                      - {version.changeNotes}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    color: colors.textPrimary,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                    bgcolor: colors.surfaceElevated,
                    p: 1,
                    borderRadius: 1,
                    width: '100%',
                    maxHeight: 100,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {version.promptText?.substring(0, 300)}
                  {version.promptText?.length > 300 && '...'}
                </Typography>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PromptLibraryPanel;
